import { useEffect, useState, useContext } from 'react';
import axios from 'api/axiosInstance';
import endpoints from 'api/endpoints';
import Tooltip from '@mui/material/Tooltip';
import { useSnackbar } from 'contexts/SnackbarContext';
import { useCompany } from 'contexts/CompanyContext';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  CircularProgress,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { StopOutlined, CheckCircleOutlined, CalendarOutlined, UserOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import styles from '../Industry/style.module.css';
import DoctorScheduleModal from './DoctorScheduleModal';
import DoctorProfileModal from './DoctorProfileModal';
import DoctorRegister from 'sections/auth/DoctorsRegistar';

// Format time to 12-hour format with AM/PM
const formatTimeWithAmPm = (time24) => {
  if (!time24) return '-';
  const [hours, minutes] = time24.slice(0, 5).split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

function CompanyAccount() {
    const { list, setList } = useCompany();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [scheduleDoctor, setScheduleDoctor] = useState(null);
  const [profileDoctor, setProfileDoctor] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const tenantId = localStorage.getItem('tenant_id');

  const fetchList = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await axios.get(endpoints.company.doctorList(), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      console.log('Doctors fetched:', response.data);
      setList(response.data || []);
      showSnackbar('Doctors loaded successfully', 'success');
    } catch (error) {
      console.error('Error fetching doctors:', error);
      showSnackbar(
        error.response?.data?.message || error.message || 'Failed to fetch doctors',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleToggleActive = async (doctorId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        endpoints.company.updateDoctor(doctorId),
        { is_active: !currentStatus },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );
      showSnackbar(`Doctor ${currentStatus ? 'deactivated' : 'activated'} successfully`, 'success');
      fetchList();
    } catch (error) {
      console.error('Error updating doctor status:', error);
      showSnackbar(error.response?.data?.message || 'Failed to update doctor status', 'error');
    }
  };

  const headCells = [
    { id: '1', align: 'center', label: 'Sl No' },
    { id: '2', align: 'center', label: 'Doctor Name' },
    { id: '3', align: 'center', label: 'Specialization' },
    { id: '4', align: 'center', label: 'Email' },
    { id: '5', align: 'center', label: 'Phone' },
    { id: '6', align: 'center', label: 'Availability' },
    { id: '7', align: 'center', label: 'Active' },
    { id: '8', align: 'center', label: 'Actions' }
  ];

  return (
    <div className={styles.container}>
      {/* Header with prominent Add Doctor CTA */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Doctors</Typography>
          <Typography variant="body2" color="text.secondary">
            {list?.length || 0} doctor{(list?.length || 0) === 1 ? '' : 's'} · manage profiles, schedules & availability
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<PlusOutlined />}
          onClick={() => setAddOpen(true)}
          sx={{
            borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 3, py: 1.25,
            boxShadow: '0 8px 22px rgba(24,144,255,0.28)',
            '&:hover': { boxShadow: '0 10px 26px rgba(24,144,255,0.4)', transform: 'translateY(-1px)' },
            transition: 'all 0.2s',
          }}
        >
          Add Doctor
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (list?.length || 0) === 0 ? (
        <Paper sx={{ borderRadius: 2, boxShadow: 3, p: 6, textAlign: 'center' }}>
          <UserOutlined style={{ fontSize: 40, color: '#94A3B8' }} />
          <Typography variant="h6" sx={{ mt: 1.5, fontWeight: 700 }}>No doctors yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Add your first doctor to start accepting appointments.
          </Typography>
          <Button variant="contained" size="large" startIcon={<PlusOutlined />} onClick={() => setAddOpen(true)}
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 3 }}>
            Add Doctor
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.align}
                    sx={{ fontWeight: 'bold', backgroundColor: '#f9fafb' }}
                  >
                    {headCell.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {list.length > 0 ? (
                list.map((row, index) => (
                  <TableRow key={row.id}>
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell align="center">{row.name || '-'}</TableCell>
                    <TableCell align="center">{row?.specialization_name || '-'}</TableCell>
                    <TableCell align="center">{row.email || '-'}</TableCell>
                    <TableCell align="center">{row.phone || '-'}</TableCell>
                    <TableCell align="center">
                      {`${formatTimeWithAmPm(row.available_from)} - ${formatTimeWithAmPm(row.available_to)}`}
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        sx={{
                          color: row.is_active ? 'green' : 'red',
                          fontWeight: 500
                        }}
                      >
                        {row.is_active ? 'Active' : 'Inactive'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit profile (photo, bio, qualifications)">
                        <IconButton color="primary" size="small" onClick={() => setProfileDoctor(row)}>
                          <UserOutlined />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit weekly schedule & branch">
                        <IconButton color="primary" size="small" onClick={() => setScheduleDoctor(row)}>
                          <CalendarOutlined />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={row.is_active ? 'Deactivate Doctor' : 'Activate Doctor'}>
                      <IconButton
                        color={row.is_active ? 'error' : 'success'}
                        size="small"
                        onClick={() => handleToggleActive(row.id, row.is_active)}
                      >
                        {row.is_active ? <StopOutlined /> : <CheckCircleOutlined />}
                      </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography>No doctors found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DoctorScheduleModal
        open={!!scheduleDoctor}
        onClose={() => setScheduleDoctor(null)}
        doctor={scheduleDoctor || {}}
        tenantId={tenantId}
      />

      <DoctorProfileModal
        open={!!profileDoctor}
        onClose={() => setProfileDoctor(null)}
        doctor={profileDoctor || {}}
        onSaved={fetchList}
      />

      {/* Add Doctor dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700 }}>
          Add Doctor
          <IconButton size="small" onClick={() => setAddOpen(false)}><CloseOutlined /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DoctorRegister onSuccess={(doc) => { setAddOpen(false); fetchList(); if (doc?.id) setScheduleDoctor(doc); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CompanyAccount;
